import { cva } from 'class-variance-authority'

/**
 * Compact Product Card CVA Configuration
 *
 * Minimalist product card for the homepage product sliders.
 * Layout: image (left) + name (right) / bottom block with reward points + price.
 *
 * Design tokens from tailwind.config.js:
 * - bg-white            — card background
 * - border-white        — default border (invisible against white bg)
 * - border-text-color   — hover border highlight
 * - text-text-color     — main text
 * - text-custom-green   — reward points accent
 * - text-custom-red     — price / discount badge accent
 * - bg-custom-red       — discount badge background
 * - border-cards-bg     — bottom block separator
 * - font-main-font      — Poppins heading font
 */

/** Outer card container — establishes group context for image hover */
export const compactProductCardContainerVariants = cva(
  'group relative flex flex-col bg-white w-full overflow-hidden border-2 border-white hover:border-text-color transition-colors duration-300'
)

/** Clickable anchor wrapping image + name */
export const compactProductCardClickableVariants = cva(
  'flex flex-col flex-1 no-underline text-text-color visited:text-text-color hover:text-text-color hover:no-underline active:text-text-color'
)

/** Row 1 grid — 2 columns (image | name) */
export const compactProductCardRowVariants = cva('grid grid-cols-2 gap-2 p-2')

/** Image frame — fixed-height container, top-aligned */
export const compactProductCardImageFrameVariants = cva(
  'flex h-[100px] xl:h-[120px] items-start justify-center overflow-hidden'
)

/** Product image — natural aspect ratio, hover scale */
export const compactProductCardImageVariants = cva(
  'max-w-full max-h-full w-auto h-auto object-contain text-[9px] leading-tight group-hover:scale-105 transition-transform duration-500'
)

/** Name column container — prevents overflow bleed */
export const compactProductCardNameContainerVariants = cva('min-w-0')

/** Primary product name <p> */
export const compactProductCardNameVariants = cva(
  'react-css font-main-font text-xs xl:text-sm font-medium leading-tight line-clamp-5 overflow-hidden'
)

/** Secondary product name (h2) <span> */
export const compactProductCardNameSecondaryVariants = cva(
  'block font-normal text-[11px] xl:text-xs text-text-color/70 leading-tight'
)

/** Bottom block — outside the link for HTML validity */
export const compactProductCardBottomVariants = cva(
  'w-full border-t border-cards-bg px-2 pt-1 pb-1.5'
)

/** Reward points row wrapper */
export const compactProductCardRewardRowVariants = cva(
  'flex items-center gap-1 text-custom-green'
)

/** Price block (price link + add-to-cart button): stacked below md, one row from md up */
export const compactProductCardPriceBlockVariants = cva(
  'flex flex-col gap-1.5 md:flex-row md:items-center md:gap-1'
)

/** Price anchor link (full width below md, 75% from md up) */
export const compactProductCardPriceLinkVariants = cva(
  'md:w-3/4 min-w-0 no-underline text-text-color visited:text-text-color hover:text-text-color hover:no-underline'
)

/** Price wrapper <div> — full width inside anchor */
export const compactProductCardPriceWrapperVariants = cva('w-full')

/** Row above the final price: old price + discount badge, or PRP. Same slot, same height. */
export const compactProductCardStrikethroughRowVariants = cva('flex items-center gap-2')

/** Strikethrough price <span> */
export const compactProductCardStrikethroughVariants = cva(
  'react-css text-[11px] xl:text-xs font-medium line-through font-main-font text-text-color/70'
)

/** PRP (msrp) <span> — the strikethrough type without the line, so both rows are equally tall */
export const compactProductCardMsrpVariants = cva(
  'react-css text-[11px] xl:text-xs font-medium font-main-font text-text-color/70'
)

/** Discount badge <span> */
export const compactProductCardDiscountBadgeVariants = cva(
  'react-css bg-custom-red px-1 text-[11px] xl:text-xs font-medium text-white font-main-font'
)

/** Final price <p> */
export const compactProductCardFinalPriceVariants = cva(
  'react-css font-main-font font-bold text-custom-red text-lg xl:text-xl leading-none whitespace-nowrap'
)

/** Price suffix <span> (e.g. "cu TVA") */
export const compactProductCardPriceSuffixVariants = cva(
  'react-css ml-1 text-[11px] xl:text-xs font-medium text-black'
)
